import { Box, FormControlLabel, ListItemText, Radio, RadioGroup, Stack, Typography } from '@mui/material';
import { PurchaseAgreement } from '@retail/purchase-agreements/types';
import { ConditionsNew } from '@retail/calculus/types';
import { LevelChip } from '@retail/calculus/components';
import { usePurchaseAgreementsTFunction } from '@retail/purchase-agreements/i18n';
import { useMemo } from 'react';
import { ScrollShadowBox } from './ScrollShadowBox';

const TWO_COLUMN_THRESHOLD = 8;

interface Props {
  agreements: PurchaseAgreement[];
  selectedAgreement?: number;
  /** Conditions that are currently active, used to flag their agreements in the list. */
  activeConditions?: ConditionsNew[];
  selectAgreement: (agreementId: number) => void;
}

export function AgreementSelect({
  agreements,
  selectedAgreement,
  activeConditions,
  selectAgreement,
}: Props) {
  const t = usePurchaseAgreementsTFunction();

  const activeByAgreementId = useMemo(() => {
    const map = new Map<number, ConditionsNew>();
    (activeConditions ?? []).forEach((condition) => {
      const agreementId = condition.purchaseCondition?.agreementId;
      if (agreementId != null) map.set(agreementId, condition);
    });
    return map;
  }, [activeConditions]);

  const twoColumns = agreements.length > TWO_COLUMN_THRESHOLD;

  if (agreements.length === 0) {
    return (
      <Typography color="text.secondary" sx={{ py: 2 }}>
        {t('purchaseAgreements.agreement.noAvailableAgreements')}
      </Typography>
    );
  }

  return (
    <ScrollShadowBox>
      <RadioGroup
        value={selectedAgreement ?? ''}
        onChange={(_, value) => selectAgreement(Number(value))}
      >
        <Box
          display="grid"
          gap={0.25}
          gridTemplateColumns={twoColumns ? { xs: '1fr', sm: 'repeat(2, 1fr)' } : '1fr'}
        >
          {agreements.map((agreement) => {
            const isSelected = agreement.id === selectedAgreement;
            const active = activeByAgreementId.get(agreement.id);

            return (
              <FormControlLabel
                key={agreement.id}
                value={agreement.id}
                control={<Radio size="small" />}
                sx={{
                  m: 0,
                  px: 1,
                  py: 0.5,
                  borderRadius: 2,
                  alignItems: 'center',
                  transition: ({ transitions }) => transitions.create('background-color'),
                  '&:hover': { backgroundColor: 'action.hover' },
                  ...(isSelected && { backgroundColor: 'action.selected' }),
                  '.MuiFormControlLabel-label': { flexGrow: 1, minWidth: 0 },
                }}
                label={
                  <Stack direction="row" alignItems="center" gap={1} width="100%">
                    <ListItemText
                      primary={agreement.name}
                      secondary={agreement.id}
                      sx={{ my: 0 }}
                      slotProps={{
                        primary: { fontWeight: 500, noWrap: true },
                        secondary: { noWrap: true },
                      }}
                    />
                    {active && (
                      <LevelChip bindingType={active.bindingType} active variant="fixed" size="small" />
                    )}
                  </Stack>
                }
              />
            );
          })}
        </Box>
      </RadioGroup>
    </ScrollShadowBox>
  );
}
